import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { Query } from '../../api'
import DashboardSkeleton from './components/DashboardSkeleton'
import { FileText, AlertTriangle, FolderOpen, Clock } from 'lucide-react'

import DashboardStatCard from './components/DashboardStatCard'
import TenderDonutChart from './components/TenderDonutChart'
import SnagPriorityChart from './components/SnagPriorityChart'
import DeadlinesList from './components/DeadlinesList'
import ActivityTimeline from './components/ActivityTimeline'
import ScheduleHealthCard from './components/ScheduleHealthCard'
import CostControlCard from '../cost-control/overview/CostControlCard'

const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id
  const projectName = meData?.me?.currentProject?.name

  const { data, loading } = useQuery(Query.DASHBOARD_OVERVIEW, {
    variables: { projectId },
    skip: !projectId,
  })

  if (!projectId) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">{t('Dashboard_no_project', 'Please select a project to view the dashboard.')}</p>
      </div>
    )
  }

  if (loading) return <DashboardSkeleton />

  const overview = data?.dashboardOverview
  if (!overview) return null

  const { tenderStats, snagStats, docStats, upcomingDeadlines, recentActivity, scheduleStats, costStats } = overview
  const showSchedule = !!scheduleStats && scheduleStats.total > 0

  // Count open snags (OPEN + REOPENED + IN_PROGRESS)
  const openSnags = snagStats.byStatus
    .filter((s: { status: string }) => ['OPEN', 'REOPENED', 'IN_PROGRESS'].includes(s.status))
    .reduce((sum: number, s: { count: number }) => sum + s.count, 0)

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-foreground">
        {t('Dashboard_overview_title', 'Dashboard')}
        {projectName && <span className="text-muted-foreground font-normal text-lg ml-2">· {projectName}</span>}
      </h1>

      {/* Top section: stats + donut chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: stat cards */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <DashboardStatCard
            value={tenderStats.total}
            label={t('Dashboard_stat_tenders', 'Tenders')}
            subtitle={t('Dashboard_stat_tenders_running', '{{count}} running', { count: tenderStats.running })}
            icon={FileText}
            colorClass="text-amber-500"
            href={`/project/${projectId}/tendering/tenders`}
          />
          <DashboardStatCard
            value={snagStats.total}
            label={t('Dashboard_stat_snags', 'Snags')}
            subtitle={t('Dashboard_stat_snags_open', '{{count}} open', { count: openSnags })}
            icon={AlertTriangle}
            colorClass="text-orange-500"
            href={`/project-setup/${projectId}/snags`}
          />
          <DashboardStatCard
            value={docStats.totalDocuments}
            label={t('Dashboard_stat_documents', 'Documents')}
            subtitle={t('Dashboard_stat_documents_new', '{{count}} new this week', { count: docStats.newDocumentsLast7Days })}
            icon={FolderOpen}
            colorClass="text-blue-500"
            href={`/project/${projectId}/documents`}
          />
        </div>

        {/* Right: donut chart */}
        <div>
          <TenderDonutChart byStatus={tenderStats.byStatus} total={tenderStats.total} />
        </div>
      </div>

      {/* Middle section: deadlines + snag priority chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {(showSchedule || costStats) && (
          <div className="flex flex-col gap-4">
            {showSchedule && scheduleStats && <ScheduleHealthCard stats={scheduleStats} projectId={projectId} />}
            {costStats && <CostControlCard stats={costStats} projectId={projectId} />}
          </div>
        )}
        <div className="lg:col-span-2">
          <DeadlinesList deadlines={upcomingDeadlines} projectId={projectId} />
        </div>
        <div>
          <SnagPriorityChart byPriority={snagStats.byPriority} total={snagStats.total} />
        </div>
      </div>

      {/* Bottom section: activity timeline */}
      <ActivityTimeline activities={recentActivity} projectId={projectId} />
    </div>
  )
}

export default Dashboard
